import UploadIcon from "@/assets/icons/Upload.svg?react";
import { useTranslation } from "@/hooks/useTranslation";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogScrollableArea,
	DialogTitle,
	Typography,
} from "@st-marketplace/ui";
import { useRef, useState } from "react";
import { usePapaParse } from "react-papaparse";

export interface ImportFileDialogProps<RowT> {
	minRows?: number;
	createEmptyRow: () => RowT;
	open: boolean;
	onClose: () => void;
	requiredColumns: (keyof RowT & string)[];
	parseRow: (raw: Record<string, any>) => RowT;
	onImport: (rows: RowT[]) => void;
	accept?: string;
}

export function ImportFileDialog<RowT>({
	minRows = 200,
	createEmptyRow,
	open,
	onClose,
	requiredColumns,
	parseRow,
	onImport,
	accept = ".csv",
}: ImportFileDialogProps<RowT>) {
	const { t } = useTranslation();
	const { readString } = usePapaParse();
	const fileInputRef = useRef<HTMLInputElement>(null);

	const [file, setFile] = useState<File | null>(null);
	const [error, setError] = useState<string | null>(null);

	const handleClose = () => {
		setFile(null);
		onClose();
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const f = e.target.files?.[0];
		if (f) setFile(f);
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]);
	};

	const openFilePicker = () => fileInputRef.current?.click();

	const parseFile = () => {
		if (!file) {
			setError(t("common.file.noFileError"));
			return;
		}

		if (file.type === "text/csv" || file.name.endsWith(".csv")) {
			const reader = new FileReader();
			reader.onload = (evt) => {
				try {
					const text = evt.target!.result as string;

					readString(text, {
						header: true,
						skipEmptyLines: true,
						complete: (parsedData) => {
							let raw = parsedData.data as Record<string, any>[];

							raw = raw.map((row) => {
								const { __parsed_extra, ...keep } = row;
								return keep;
							});

							if (raw.length === 0) {
								setError(t("common.file.emptySheetError"));
								return;
							}

							const headers = Object.keys(raw[0]);
							console.info(headers);
							const missing = requiredColumns.filter(
								(c) => !headers.includes(c),
							);
							if (missing.length) {
								setError(
									t("common.file.missingColsError", {
										cols: missing.join(", "),
									}),
								);
								return;
							}

							const parsed: RowT[] = raw.map(parseRow);

							const rows: RowT[] =
								parsed.length < minRows
									? [
											...parsed,
											...Array.from({ length: minRows - parsed.length }, () =>
												createEmptyRow(),
											),
										]
									: parsed;

							onImport(rows);
							handleClose();
						},
						error: (err) => {
							console.error("CSV Parsing Error: ", err);
							setError(t("common.file.parseError"));
						},
					});
				} catch (err) {
					console.error("File Reading Error: ", err);
					setError(t("common.file.parseError"));
				}
			};
			reader.readAsText(file);
		} else {
			setError(t("common.file.invalidFileType"));
		}
	};

	return (
		<>
			<Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
				<DialogContent className="w-full max-w-2xl">
					<DialogHeader className="flex-row items-start justify-between gap-4">
						<div>
							<DialogTitle>{t("common.file.importTitle")}</DialogTitle>
							<DialogDescription>
								{t("common.file.importSubtitle")}
							</DialogDescription>
						</div>
					</DialogHeader>

					<DialogScrollableArea className="max-h-[90vh]">
						<div
							role="button"
							tabIndex={0}
							onClick={openFilePicker}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									openFilePicker();
								}
							}}
							onDragOver={handleDragOver}
							onDrop={handleDrop}
							className="flex cursor-pointer flex-col items-center rounded-xl border-2 border-dashed border-secondary-border p-5 py-8"
						>
							<input
								ref={fileInputRef}
								type="file"
								hidden
								accept={accept}
								onChange={handleFileChange}
							/>
							<UploadIcon className="mb-1 size-12 text-primary-100" />
							<Typography variant="title-sm" className="mb-2">
								{t("common.file.importUpload")}{" "}
								<Typography variant="title-sm" as="span">
									{t("common.file.importDrag")}
								</Typography>
							</Typography>
							<Typography variant="body-sm">
								{t("common.file.importMaxSize")}
							</Typography>

							{file && (
								<Typography variant="body-sm" className="mt-2 text-primary-100">
									{t("common.file.importSelected")}: {file.name}
								</Typography>
							)}
						</div>
					</DialogScrollableArea>

					<DialogFooter
						buttons={{
							secondary: {
								label: t("common.button.cancel"),
								props: { type: "button", onClick: handleClose },
							},
							primary: {
								label: t("common.button.attach"),
								props: { type: "button", onClick: parseFile },
							},
						}}
					/>
				</DialogContent>
			</Dialog>

			{/* error dialog */}
			<Dialog open={!!error} onOpenChange={(o) => !o && setError(null)}>
				<DialogContent className="w-full max-w-120">
					<DialogHeader>
						<DialogTitle className="text-danger-text">
							{t("common.error.title")}
						</DialogTitle>
					</DialogHeader>
					<DialogScrollableArea>
						<Typography variant="body-md">{error}</Typography>
					</DialogScrollableArea>
					<DialogFooter
						buttons={{
							secondary: {
								label: t("common.button.close"),
								props: { type: "button", onClick: () => setError(null) },
							},
						}}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
}
